// src/components/DeleteAccount.jsx
// The end of the road: deleting the account with everything in it (or, for a guest, the guest's tournaments).
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { deleteAccount, deleteGuestData } from '../account';
import { usesPassword } from '../auth';
import { auth } from '../firebase';
import { signInErrorMessage } from '../pages/Login';

const WORD = 'DELETE';

function DeleteAccount({ user }) {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [typed, setTyped] = useState('');
    const [password, setPassword] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const guest = Boolean(user.isGuest);
    const needsPassword = !guest && usesPassword(auth.currentUser);

    const close = () => {
        if (busy) return;
        setOpen(false);
        setTyped('');
        setPassword('');
        setError(null);
    };

    const confirm = async (event) => {
        event.preventDefault();
        setBusy(true);
        setError(null);
        try {
            if (guest) await deleteGuestData();
            else await deleteAccount({ password });
            navigate('/');
        } catch (deleteError) {
            console.error('Could not delete the account:', deleteError);
            setError(
                deleteError.status !== undefined
                    ? `Could not delete the data on the server. ${deleteError.message}`
                    : deleteError.code
                      ? signInErrorMessage(deleteError)
                      : 'This needs a connection to the server. Try again when you are online.'
            );
            setBusy(false);
        }
    };

    return (
        <section className="card account-section" aria-label="Delete account">
            <h2>{guest ? 'Delete guest tournaments' : 'Delete account'}</h2>
            <p className="hint">
                {guest
                    ? 'You are using the app as a guest: your tournaments are only on this device.'
                    : 'Deleting your account removes your tournaments, backups and sign-in for good.'}
            </p>
            <button className="btn btn-danger-text" onClick={() => setOpen(true)}>
                {guest ? 'Delete my guest tournaments' : 'Delete my account'}
            </button>
            {open &&
                createPortal(
                    <div className="modal-backdrop" onClick={close}>
                        <form
                            className="modal"
                            role="dialog"
                            aria-modal="true"
                            aria-labelledby="delete-account-title"
                            onClick={(event) => event.stopPropagation()}
                            onSubmit={confirm}
                        >
                            <h2 id="delete-account-title">{guest ? 'Delete your guest tournaments?' : 'Delete your account?'}</h2>
                            {guest ? (
                                <p>All tournaments you made as a guest are removed from this device. This cannot be undone.</p>
                            ) : (
                                <>
                                    <p>This cannot be undone. What goes:</p>
                                    <ul className="delete-list">
                                        <li>all your tournaments, players and scores, and their backups</li>
                                        <li>public links you made (they stop working)</li>
                                        <li>tournaments you share with co-organizers <strong>and own</strong>, for them too</li>
                                        <li>your place in tournaments others run: you just leave those</li>
                                        <li>your sign-in itself</li>
                                    </ul>
                                </>
                            )}
                            {needsPassword && (
                                <input
                                    className="input delete-field"
                                    type="password"
                                    autoComplete="current-password"
                                    placeholder="Your password"
                                    aria-label="Your password"
                                    value={password}
                                    onChange={(event) => setPassword(event.target.value)}
                                    required
                                />
                            )}
                            <label className="hint" htmlFor="delete-confirm">
                                Type {WORD} to confirm
                            </label>
                            <input
                                id="delete-confirm"
                                className="input delete-field"
                                autoComplete="off"
                                autoCapitalize="characters"
                                value={typed}
                                onChange={(event) => setTyped(event.target.value)}
                            />
                            {!guest && !needsPassword && (
                                <p className="hint">Your sign-in provider (Google or Apple) will ask you to confirm once more.</p>
                            )}
                            {error && (
                                <p className="form-error" role="alert">
                                    {error}
                                </p>
                            )}
                            <div className="modal-actions">
                                <button type="button" className="btn" onClick={close} disabled={busy}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-danger" disabled={busy || typed.trim() !== WORD}>
                                    {guest ? 'Delete' : 'Delete everything'}
                                </button>
                            </div>
                        </form>
                    </div>,
                    document.body
                )}
        </section>
    );
}

export default DeleteAccount;
